// @ts-check
// Canvas horizontal do editor (spec 2.0.0, fluxos.md seção 20.5; contrato 09 de fluxos, seção 17): a área rolável
// com o cartão de entrada e a árvore, o botão "Ações ›" no canto superior direito e a barra de zoom no rodapé
// ("−", o percentual, "+", "Centralizar" e "Números"). Arrastar o fundo rola (mão), Shift e a roda rolam na horizontal,
// Ctrl ou Cmd e a roda mudam o zoom mantendo o ponto sob o ponteiro. Uma parada de Tab: dentro, as setas andam pela
// árvore (client/src/lib/flow-canvas-keyboard.js), Enter abre a configuração, A ou Insert abre o catálogo depois do
// passo, Delete exclui, Esc fecha o painel e +, - e 0 mudam o zoom. Os bots (contrato de bots, seção 22.2) usam o mesmo
// canvas com o próprio cartão de entrada, a própria navegação por teclado e o texto do botão de ações.
import { html } from 'htm/preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { FLOW_ENTRY_FOCUS, moveFlowFocus } from '../../lib/flow-canvas-keyboard.js';
import { canZoomIn, canZoomOut, flowZoomPercent, scrollKeepingPoint, zoomInLevel, zoomOutLevel, FLOW_CENTER_LEFT_PIXELS, FLOW_DEFAULT_ZOOM } from '../../lib/flow-zoom.js';
import { FlowTreeContext, FlowList } from './flow-tree.js';
import { FlowEntryCard } from './flow-entry-card.js';
import { isTypingTarget } from '../../lib/keyboard-shortcuts.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('./flow-tree.js').FlowTreeContextValue} FlowTreeContextValue
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 */

const SUPPORTS_CSS_ZOOM = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1');
const NAVIGATION_KEYS = new Set(['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End']);
// o que não arrasta o fundo: cartões, botões, links e campos
const INTERACTIVE_SELECTOR = '.flow-node-card, .flow-entry-card, button, a, input, select, textarea, .flow-node-counts';

/**
 * @param {{ definition: FlowDefinition, tree: Omit<FlowTreeContextValue, 'focusTarget'>, entryProblems: FlowProblem[],
 *   entryCounts: { entered: number, inFlow: number } | null, focusTarget: string, onFocusTarget: (target: string) => void,
 *   zoom: number, onZoom: (zoom: number) => void, showCounts: boolean, canShowCounts: boolean, onToggleCounts: () => void,
 *   onOpenActions?: () => void, onActivateTarget?: (target: string) => void, onInsertAfterTarget?: (target: string) => void,
 *   onDeleteTarget?: (target: string) => void, onEscape?: () => void, onEditEntry?: () => void, onSetManualOnly?: () => void,
 *   isEntrySelected?: boolean, ariaLabel: string, announcement?: string, isReadOnly?: boolean, centerSignal?: number, className?: string,
 *   renderEntry?: ((args: { isFocused: boolean }) => any) | null, moveFocus?: ((definition: any, focus: string, key: any) => string) | null,
 *   actionsLabel?: string | null }} props
 *   renderEntry, moveFocus e actionsLabel: o cartão de entrada, as setas e o botão "Ações" de outra área (os bots)
 */
export function FlowCanvas({
  definition, tree, entryProblems, entryCounts, focusTarget, onFocusTarget, zoom, onZoom, showCounts, canShowCounts, onToggleCounts, onOpenActions,
  onActivateTarget, onInsertAfterTarget, onDeleteTarget, onEscape, onEditEntry, onSetManualOnly, isEntrySelected = false, ariaLabel, announcement = '',
  isReadOnly = false, centerSignal = 0, className = '', renderEntry = null, moveFocus = null, actionsLabel = null,
}) {
  const viewportRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const contentRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const panRef = useRef(/** @type {{ x: number, y: number, left: number, top: number } | null} */ (null));
  const keyboardMoveRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const [scaledSize, setScaledSize] = useState(/** @type {{ width: number, height: number } | null} */ (null));

  /** Rola para o cartão de entrada ficar perto da esquerda, com a linha principal no meio da altura. */
  const center = () => {
    const viewport = viewportRef.current;
    const entry = /** @type {HTMLElement | null} */ (viewport?.querySelector('[data-focus-id="entry"]') ?? null);
    if (!viewport || !entry) return;
    const viewportRect = viewport.getBoundingClientRect();
    const entryRect = entry.getBoundingClientRect();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    viewport.scrollTo({
      left: Math.max(0, entryRect.left - viewportRect.left + viewport.scrollLeft - FLOW_CENTER_LEFT_PIXELS),
      top: Math.max(0, entryRect.top - viewportRect.top + viewport.scrollTop + entryRect.height / 2 - viewport.clientHeight / 2),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  useLayoutEffect(() => {
    center();
  }, [centerSignal]);

  // sem a propriedade zoom do CSS (navegador antigo), escala por transform num invólucro com o tamanho medido
  useEffect(() => {
    if (SUPPORTS_CSS_ZOOM || !contentRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const content = contentRef.current;
    const observer = new ResizeObserver(() => setScaledSize({ width: content.offsetWidth * zoom, height: content.offsetHeight * zoom }));
    observer.observe(content);
    return () => observer.disconnect();
  }, [zoom]);

  // foco que andou pelo teclado: o elemento ganha o foco e entra na área visível
  useEffect(() => {
    if (!keyboardMoveRef.current) return;
    keyboardMoveRef.current = false;
    const element = /** @type {HTMLElement | null} */ (viewportRef.current?.querySelector(`[data-focus-id="${CSS.escape(focusTarget)}"]`) ?? null);
    element?.focus({ preventScroll: true });
    element?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [focusTarget]);

  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (isTypingTarget(event.target)) return;
    const hasModifier = event.ctrlKey || event.metaKey;
    if (NAVIGATION_KEYS.has(event.key) && !hasModifier) {
      event.preventDefault();
      keyboardMoveRef.current = true;
      onFocusTarget((moveFocus ?? moveFlowFocus)(definition, focusTarget, /** @type {any} */ (event.key)));
      return;
    }
    if (hasModifier || event.altKey) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivateTarget?.(focusTarget);
    } else if ((event.key === 'a' || event.key === 'A' || event.key === 'Insert') && !isReadOnly) {
      event.preventDefault();
      onInsertAfterTarget?.(focusTarget);
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && !isReadOnly && focusTarget !== FLOW_ENTRY_FOCUS) {
      event.preventDefault();
      onDeleteTarget?.(focusTarget);
    } else if (event.key === 'Escape') {
      onEscape?.();
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      onZoom(zoomInLevel(zoom));
    } else if (event.key === '-') {
      event.preventDefault();
      onZoom(zoomOutLevel(zoom));
    } else if (event.key === '0') {
      event.preventDefault();
      onZoom(FLOW_DEFAULT_ZOOM);
    }
  };

  /** @param {WheelEvent} event */
  const handleWheel = (event) => {
    const viewport = viewportRef.current;
    if (!viewport || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    const next = event.deltaY < 0 ? zoomInLevel(zoom) : zoomOutLevel(zoom);
    if (next === zoom) return;
    const rect = viewport.getBoundingClientRect();
    const target = scrollKeepingPoint({
      scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop, pointerX: event.clientX - rect.left, pointerY: event.clientY - rect.top, from: zoom, to: next,
    });
    onZoom(next);
    requestAnimationFrame(() => viewport.scrollTo(target.scrollLeft, target.scrollTop));
  };

  // a roda com Ctrl precisa de ouvinte não passivo para impedir o zoom da página
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  });

  /** @param {MouseEvent} event */
  const startPan = (event) => {
    const viewport = viewportRef.current;
    if (!viewport || event.button !== 0 || /** @type {HTMLElement} */ (event.target).closest?.(INTERACTIVE_SELECTOR)) return;
    panRef.current = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    setIsPanning(true);
    const move = (/** @type {MouseEvent} */ moveEvent) => {
      const start = panRef.current;
      if (!start) return;
      viewport.scrollLeft = start.left - (moveEvent.clientX - start.x);
      viewport.scrollTop = start.top - (moveEvent.clientY - start.y);
    };
    const stop = () => {
      panRef.current = null;
      setIsPanning(false);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
  };

  /** @type {Record<string, string>} */
  const contentStyle = SUPPORTS_CSS_ZOOM ? { zoom: String(zoom) } : { transform: `scale(${zoom})`, transformOrigin: '0 0' };
  const content = html`<div class="flow-canvas-content" ref=${contentRef} style=${contentStyle}>
    <div class="flow-row" role="tree" aria-label=${ariaLabel} onKeyDown=${handleKeyDown}
      onFocusIn=${(/** @type {FocusEvent} */ event) => {
        const id = /** @type {HTMLElement} */ (event.target).closest?.('[data-focus-id]')?.getAttribute('data-focus-id');
        if (id && id !== focusTarget) onFocusTarget(id);
      }}>
      ${renderEntry
        ? renderEntry({ isFocused: focusTarget === FLOW_ENTRY_FOCUS })
        : html`<${FlowEntryCard} entry=${definition.entry} lookups=${tree.lookups} problems=${entryProblems} isFocused=${focusTarget === FLOW_ENTRY_FOCUS}
            isReadOnly=${isReadOnly} isSelected=${isEntrySelected} counts=${showCounts ? entryCounts : null} flowId=${tree.flowId}
            onEdit=${onEditEntry} onSetManualOnly=${onSetManualOnly} />`}
      <${FlowTreeContext.Provider} value=${{ ...tree, focusTarget }}>
        <${FlowList} list=${definition.nodes} parentId=${null} branchKey=${null} depth=${0} />
      <//>
    </div>
  </div>`;

  return html`<div class=${`flow-canvas ${className}`} data-counts=${showCounts ? 'true' : undefined} data-readonly=${isReadOnly ? 'true' : undefined}>
    <div class="flow-canvas-viewport" ref=${viewportRef} data-panning=${isPanning ? 'true' : undefined} onMouseDown=${startPan}>
      ${SUPPORTS_CSS_ZOOM ? content : html`<div class="flow-canvas-scaled" style=${scaledSize ? { width: `${scaledSize.width}px`, height: `${scaledSize.height}px` } : {}}>${content}</div>`}
    </div>
    ${onOpenActions && !isReadOnly
      ? html`<button type="button" class="flow-actions-button" onClick=${onOpenActions}><span>${actionsLabel ?? t('flows.canvas.actions')}</span><${Icon} name="IconChevronRight" size=${16} /></button>`
      : null}
    <div class="flow-zoom-toolbar" role="toolbar" aria-label=${t('flows.canvas.zoomToolbar')}>
      <button type="button" class="flow-zoom-button" aria-label=${t('flows.canvas.zoomOut')} title=${t('flows.canvas.zoomOut')} disabled=${!canZoomOut(zoom)}
        onClick=${() => onZoom(zoomOutLevel(zoom))}><${Icon} name="IconMinus" size=${14} /></button>
      <span class="flow-zoom-value" aria-live="polite">${flowZoomPercent(zoom)}</span>
      <button type="button" class="flow-zoom-button" aria-label=${t('flows.canvas.zoomIn')} title=${t('flows.canvas.zoomIn')} disabled=${!canZoomIn(zoom)}
        onClick=${() => onZoom(zoomInLevel(zoom))}><${Icon} name="IconPlus" size=${14} /></button>
      <span class="flow-zoom-divider" aria-hidden="true"></span>
      <button type="button" class="flow-zoom-button" data-wide="true" onClick=${center} title=${t('flows.canvas.center')}>
        <${Icon} name="IconZoomScan" size=${14} /><span>${t('flows.canvas.center')}</span>
      </button>
      ${canShowCounts
        ? html`<button type="button" class="flow-zoom-button" data-wide="true" aria-pressed=${showCounts ? 'true' : 'false'} onClick=${onToggleCounts}
            title=${t('flows.canvas.showCounts')}><${Icon} name="IconHash" size=${14} /><span>${t('flows.canvas.counts')}</span></button>`
        : null}
    </div>
    <div class="visually-hidden-text" aria-live="polite">${announcement}</div>
  </div>`;
}
